import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  ArrowLeft,
  Buildings,
  Users,
  WarningCircle,
  Trash,
  PencilSimple,
  Plus,
  CheckCircle,
  XCircle,
  Pause,
  X,
  ArrowsClockwise
} from '@phosphor-icons/react';
import type { Icon } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { getOrganization, updateOrganization, deleteOrganization, updateUser, createTenant, getSuperAdminData, invalidateCommHubUsersCache, clearCache } from '../lib/api';

// Type definitions
interface OrgData {
  id: string;
  name?: string;
  org_number?: string;
  address?: string;
  billing_email?: string;
  subscription_plan?: string;
  subscription_status?: string;
  max_users?: number;
  max_properties?: number;
  api_keys?: string[];
  contact_email?: string;
  contact_phone?: string;
  trial_ends_at?: string;
  is_active?: boolean;
  created_at?: string;
  updated_at?: string;
}

interface UserData {
  id: string;
  name?: string;
  email?: string;
  phone?: string;
  role?: string;
  organization_id?: string;
  is_active?: boolean;
}

interface OrgStats {
  userCount?: number;
  propertyCount?: number;
  apartmentCount?: number;
  issueCount?: number;
  issues_open?: number;
  issues_total?: number;
  users?: number;
  properties?: number;
  apartments?: number;
}

interface FormDataType {
  name?: string;
  address?: string;
  org_number?: string;
  billing_email?: string;
  contact_email?: string;
  contact_phone?: string;
  subscription_plan?: string;
  subscription_status?: string;
  max_users?: number;
  max_properties?: number;
}

export default function OrganizationDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [org, setOrg] = useState<OrgData | null>(null);
  const [orgUsers, setOrgUsers] = useState<UserData[]>([]);
  const [orgStats, setOrgStats] = useState<OrgStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [addUserOpen, setAddUserOpen] = useState(false);
  const [editUserOpen, setEditUserOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<UserData | null>(null);
  const [formData, setFormData] = useState<FormDataType>({});
  const [newUser, setNewUser] = useState<{
    email: string;
    name: string;
    phone: string;
    role: 'superadmin' | 'admin' | 'auditor' | 'suppliant' | 'board_member' | 'resident' | 'supplier' | 'external';
  }>({
    email: '',
    name: '',
    phone: '',
    role: 'admin'
  });

  const fetchOrg = async () => {
    try {
      // Single batch request for ALL data!
      const { organizations, users, properties, issues, apartments } = await getSuperAdminData();
      
      // Find this organization
      const data = organizations.find(o => o.id === id);
      if (data) {
        setOrg(data);
        setFormData(data);
        
        // Filter users for this organization
        // Only include users that explicitly have this organization_id
        const filteredUsers = users.filter(u => {
          // Must have organization_id matching this org
          return u.organization_id === id;
        });
        
        console.log(`[OrganizationDetail] Org: ${data.name} (${id})`);
        console.log(`[OrganizationDetail] Total users: ${users.length}`);
        console.log(`[OrganizationDetail] Filtered users: ${filteredUsers.length}`);
        console.log(`[OrganizationDetail] Users with no org_id:`, users.filter(u => !u.organization_id).map(u => u.email));
        
        setOrgUsers(filteredUsers);
        
        // Calculate stats for this organization
        setOrgStats({
          users: filteredUsers.length,
          properties: properties.filter(p => p.organization_id === id).length,
          apartments: apartments.filter(a => a.organization_id === id).length,
          issues_open: issues.filter(i => i.organization_id === id && (i.status === 'open' || i.status === 'in_progress')).length,
          issues_total: issues.filter(i => i.organization_id === id).length
        });
      } else if (id) {
        // If not in batch, try direct fetch
        const orgRes = await getOrganization(id);
        if (orgRes.data) {
          setOrg(orgRes.data);
          setFormData(orgRes.data);
        } else {
          navigate('/superadmin');
        }
      }
    } catch (error) {
      console.error('Failed to fetch organization:', error);
      navigate('/superadmin');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrg();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Force refresh - clears all caches and re-fetches
  const handleForceRefresh = async () => {
    setLoading(true);
    invalidateCommHubUsersCache();
    clearCache();
    await fetchOrg();
    toast.success('Data uppdaterad');
  };

  const handleSave = async (): Promise<void> => {
    if (!id) return;
    try {
      const { data } = await updateOrganization(id, formData);
      setOrg({ ...org, ...data });
      setEditing(false);
      toast.success('Föreningen har uppdaterats');
    } catch (error) {
      toast.error('Kunde inte spara');
    }
  };

  const handleDelete = async (): Promise<void> => {
    if (!id) return;
    if (!window.confirm('Är du säker? Detta raderar föreningen och ALL dess data permanent.')) return;
    
    try {
      await deleteOrganization(id);
      toast.success('Föreningen har raderats');
      navigate('/superadmin/organizations');
    } catch (error) {
      toast.error('Kunde inte radera');
    }
  };

  const handleAddUser = async (): Promise<void> => {
    if (!newUser.email || !newUser.name) {
      toast.error('Fyll i namn och e-post');
      return;
    }
    
    try {
      await createTenant({
        ...newUser,
        organization_id: id
      });
      toast.success('Användare tillagd');
      setAddUserOpen(false);
      setNewUser({ email: '', name: '', phone: '', role: 'admin' });
      fetchOrg();
    } catch (error) {
      toast.error('Kunde inte lägga till användare');
    }
  };

  const handleEditUser = async (): Promise<void> => {
    if (!editingUser || !editingUser.name || !editingUser.email) {
      toast.error('Fyll i namn och e-post');
      return;
    }
    
    try {
      await updateUser(editingUser.id, {
        name: editingUser.name,
        email: editingUser.email,
        phone: editingUser.phone,
        role: editingUser.role as 'superadmin' | 'admin' | 'auditor' | 'suppliant' | 'board_member' | 'resident' | 'supplier' | 'external'
      });
      toast.success('Användare uppdaterad');
      setEditUserOpen(false);
      setEditingUser(null);
      fetchOrg();
    } catch (error) {
      toast.error('Kunde inte uppdatera användare');
    }
  };

  const handleRemoveUser = async (userId: string): Promise<void> => {
    const user = orgUsers.find(u => u.id === userId);
    const userName = user?.name || user?.email || 'denna användare';
    
    if (!window.confirm(`Är du säker på att du vill ta bort "${userName}" från föreningen?\n\nAnvändaren kommer fortfarande finnas kvar i systemet men inte tillhöra denna förening.`)) return;
    
    try {
      // Set organization_id to null to remove from organization
      await updateUser(userId, { organization_id: undefined });
      toast.success(`${userName} borttagen från föreningen`);
      fetchOrg();
    } catch (error) {
      console.error('Error removing user:', error);
      toast.error('Kunde inte ta bort användare från föreningen');
    }
  };

  const openEditUser = (user: UserData): void => {
    setEditingUser({ ...user });
    setEditUserOpen(true);
  };

  const getStatusBadge = (status: string | undefined): { class: string; icon: Icon } => {
    const badges: Record<string, { class: string; icon: Icon }> = {
      active: { class: 'badge-success', icon: CheckCircle },
      trial: { class: 'badge-warning', icon: CheckCircle },
      suspended: { class: 'badge-emergency', icon: Pause },
      cancelled: { class: 'badge-neutral', icon: XCircle }
    };
    return badges[status || ''] || badges.active;
  };

  if (loading) {
    return (
      <div className="p-6 md:p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-6 w-32 bg-slate-200 rounded" />
          <div className="h-8 w-64 bg-slate-200 rounded" />
          <div className="card-flat">
            <div className="h-4 w-full bg-slate-200 rounded mb-2" />
            <div className="h-4 w-2/3 bg-slate-200 rounded" />
          </div>
        </div>
      </div>
    );
  }

  if (!org) return null;

  const statusInfo = getStatusBadge(org.subscription_status);
  const StatusIcon = statusInfo.icon;

  return (
    <div className="p-6 md:p-8 max-w-4xl" data-testid="org-detail-page">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/superadmin/organizations')}
          className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700 mb-4"
        >
          <ArrowLeft size={16} />
          Föreningar
        </button>
        
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-md bg-slate-100 flex items-center justify-center">
              <Buildings size={24} className="text-slate-600" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
                {org.name}
              </h1>
              <div className="flex items-center gap-2 mt-1">
                <span className={statusInfo.class}>
                  <StatusIcon size={12} className="mr-1" />
                  {org.subscription_status}
                </span>
                <span className="text-sm text-slate-500">
                  {org.subscription_plan} plan
                </span>
              </div>
            </div>
          </div>
          
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setEditing(!editing)}
            >
              <PencilSimple size={16} className="mr-2" />
              {editing ? 'Avbryt' : 'Redigera'}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleDelete}
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              <Trash size={16} className="mr-2" />
              Radera
            </Button>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Stats */}
          {orgStats && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="stat-card">
                <div className="stat-value">{orgStats.users}</div>
                <div className="stat-label">Användare</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">{orgStats.properties}</div>
                <div className="stat-label">Fastigheter</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">{orgStats.apartments}</div>
                <div className="stat-label">Lägenheter</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">{orgStats.issues_open}</div>
                <div className="stat-label">Öppna ärenden</div>
              </div>
            </div>
          )}

          {/* Organization Details */}
          <div className="card-flat">
            <h2 className="text-base font-medium text-slate-900 mb-4">Uppgifter</h2>
            
            {editing ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Namn</Label>
                    <Input
                      value={formData.name || ''}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Org.nummer</Label>
                    <Input
                      value={formData.org_number || ''}
                      onChange={(e) => setFormData({ ...formData, org_number: e.target.value })}
                      className="font-mono"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Adress</Label>
                  <Input
                    value={formData.address || ''}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Kontakt-e-post</Label>
                    <Input
                      type="email"
                      value={formData.contact_email || ''}
                      onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Kontakttelefon</Label>
                    <Input
                      value={formData.contact_phone || ''}
                      onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                    />
                  </div>
                </div>
                <Button onClick={handleSave} className="w-full">Spara</Button>
              </div>
            ) : (
              <dl className="space-y-4 text-sm">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <dt className="text-slate-500">Org.nummer</dt>
                    <dd className="font-mono text-slate-900">{org.org_number || '-'}</dd>
                  </div>
                  <div>
                    <dt className="text-slate-500">Adress</dt>
                    <dd className="text-slate-900">{org.address || '-'}</dd>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <dt className="text-slate-500">Kontakt-e-post</dt>
                    <dd className="text-slate-900">{org.contact_email || '-'}</dd>
                  </div>
                  <div>
                    <dt className="text-slate-500">Kontakttelefon</dt>
                    <dd className="text-slate-900">{org.contact_phone || '-'}</dd>
                  </div>
                </div>
                <div>
                  <dt className="text-slate-500">Skapad</dt>
                  <dd className="text-slate-900">{org.created_at ? new Date(org.created_at).toLocaleDateString() : '-'}</dd>
                </div>
              </dl>
            )}
          </div>

          {/* Account Managers */}
          <div className="card-flat">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-medium text-slate-900">
                Kontoansvariga ({orgUsers.length})
              </h2>
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={handleForceRefresh} title="Uppdatera lista">
                  <ArrowsClockwise size={16} />
                </Button>
                <Dialog open={addUserOpen} onOpenChange={setAddUserOpen}>
                  <DialogTrigger asChild>
                    <Button variant="outline" size="sm">
                      <Plus size={16} className="mr-2" />
                      Lägg till
                    </Button>
                  </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Lägg till kontoansvarig</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label>Namn *</Label>
                      <Input
                        value={newUser.name}
                        onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>E-post *</Label>
                      <Input
                        type="email"
                        value={newUser.email}
                        onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Telefon</Label>
                      <Input
                        value={newUser.phone}
                        onChange={(e) => setNewUser({ ...newUser, phone: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Roll</Label>
                      <Select
                        value={newUser.role}
                        onValueChange={(value) => setNewUser({ ...newUser, role: value as typeof newUser.role })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="admin">Admin</SelectItem>
                          <SelectItem value="auditor">Revisor</SelectItem>
                          <SelectItem value="suppliant">Suppleant</SelectItem>
                          <SelectItem value="board_member">Styrelsemedlem</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <Button onClick={handleAddUser} className="w-full">Lägg till</Button>
                  </div>
                </DialogContent>
              </Dialog>
              </div>
            </div>
            
            {orgUsers.length === 0 ? (
              <p className="text-sm text-slate-500">Inga kontoansvariga ännu</p>
            ) : (
              <div className="space-y-2">
                {orgUsers.map((user) => (
                  <div
                    key={user.id}
                    className="flex items-center gap-3 p-3 bg-slate-50 rounded-md"
                  >
                    <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center">
                      <Users size={16} className="text-slate-500" />
                    </div>
                    <div className="flex-1">
                      <div className="text-sm font-medium text-slate-900">{user.name}</div>
                      <div className="text-xs text-slate-500">{user.email}</div>
                      {user.phone && <div className="text-xs text-slate-400">{user.phone}</div>}
                    </div>
                    <span className="text-xs px-2 py-1 bg-slate-200 rounded-md">
                      {user.role === 'admin' ? 'Admin' : user.role === 'board_member' ? 'Styrelse' : user.role}
                    </span>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => openEditUser(user)}
                        className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                        title="Redigera"
                      >
                        <PencilSimple size={16} />
                      </button>
                      <button
                        onClick={() => handleRemoveUser(user.id)}
                        className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded"
                        title="Ta bort"
                      >
                        <Trash size={16} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Edit User Dialog */}
            <Dialog open={editUserOpen} onOpenChange={setEditUserOpen}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Redigera kontoansvarig</DialogTitle>
                </DialogHeader>
                {editingUser && (
                  <div className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label>Namn *</Label>
                      <Input
                        value={editingUser.name || ''}
                        onChange={(e) => setEditingUser({ ...editingUser, name: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>E-post *</Label>
                      <Input
                        type="email"
                        value={editingUser.email || ''}
                        onChange={(e) => setEditingUser({ ...editingUser, email: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Telefon</Label>
                      <Input
                        value={editingUser.phone || ''}
                        onChange={(e) => setEditingUser({ ...editingUser, phone: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Roll</Label>
                      <Select
                        value={editingUser.role || 'admin'}
                        onValueChange={(value) => setEditingUser({ ...editingUser, role: value })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="admin">Admin</SelectItem>
                          <SelectItem value="auditor">Revisor</SelectItem>
                          <SelectItem value="suppliant">Suppleant</SelectItem>
                          <SelectItem value="board_member">Styrelsemedlem</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex gap-2">
                      <Button onClick={handleEditUser} className="flex-1">Spara</Button>
                      <Button variant="outline" onClick={() => setEditUserOpen(false)}>Avbryt</Button>
                    </div>
                  </div>
                )}
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Sidebar - Subscription */}
        <div className="space-y-6">
          <div className="card-flat">
            <h2 className="text-base font-medium text-slate-900 mb-4">Abonnemang</h2>
            
            {editing ? (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Plan</Label>
                  <Select
                    value={formData.subscription_plan}
                    onValueChange={(value) => setFormData({ ...formData, subscription_plan: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="basic">Basic</SelectItem>
                      <SelectItem value="standard">Standard</SelectItem>
                      <SelectItem value="premium">Premium</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select
                    value={formData.subscription_status}
                    onValueChange={(value) => setFormData({ ...formData, subscription_status: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Aktiv</SelectItem>
                      <SelectItem value="trial">Trial</SelectItem>
                      <SelectItem value="suspended">Avstängd</SelectItem>
                      <SelectItem value="cancelled">Avslutad</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Max användare</Label>
                    <Input
                      type="number"
                      value={formData.max_users || 50}
                      onChange={(e) => setFormData({ ...formData, max_users: parseInt(e.target.value) })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Max fastigheter</Label>
                    <Input
                      type="number"
                      value={formData.max_properties || 5}
                      onChange={(e) => setFormData({ ...formData, max_properties: parseInt(e.target.value) })}
                    />
                  </div>
                </div>
              </div>
            ) : (
              <dl className="space-y-3 text-sm">
                <div>
                  <dt className="text-slate-500">Plan</dt>
                  <dd className="font-medium text-slate-900 capitalize">{org.subscription_plan}</dd>
                </div>
                <div>
                  <dt className="text-slate-500">Max användare</dt>
                  <dd className="text-slate-900">{org.max_users}</dd>
                </div>
                <div>
                  <dt className="text-slate-500">Max fastigheter</dt>
                  <dd className="text-slate-900">{org.max_properties}</dd>
                </div>
                {org.trial_ends_at && (
                  <div>
                    <dt className="text-slate-500">Trial slutar</dt>
                    <dd className="text-slate-900">{new Date(org.trial_ends_at).toLocaleDateString()}</dd>
                  </div>
                )}
              </dl>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
